import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Volume2, VolumeX, Sparkles, Loader2, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getOrCreateSession = () => {
  let s = sessionStorage.getItem('help_chat_session');
  if (!s) {
    s = 'help_' + Math.random().toString(36).substring(2, 12) + '_' + Date.now();
    sessionStorage.setItem('help_chat_session', s);
  }
  return s;
};

const HelpChatbot = () => {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const sessionId = useRef(getOrCreateSession());
  const scrollRef = useRef(null);
  const synthRef = useRef(typeof window !== 'undefined' ? window.speechSynthesis : null);

  // Initial greeting
  useEffect(() => {
    const lang = i18n.language || 'es';
    const greetings = {
      es: '¡Hola! 👋 Soy tu asistente de A Gift of Memories. ¿En qué puedo ayudarte hoy? Puedes preguntarme sobre precios, cómo crear tu libro, invitar colaboradores, plazos de entrega, o cualquier duda sobre el proceso.',
      en: "Hi! 👋 I'm your A Gift of Memories assistant. How can I help you today? Feel free to ask about pricing, how to create your book, inviting contributors, delivery times, or any question about the process.",
      fr: "Bonjour ! 👋 Je suis votre assistant A Gift of Memories. Comment puis-je vous aider aujourd'hui ? N'hésitez pas à me poser des questions sur les prix, la création du livre, les contributeurs, les délais, etc.",
      de: 'Hallo! 👋 Ich bin Ihr A Gift of Memories-Assistent. Wie kann ich Ihnen heute helfen? Fragen Sie mich gerne zu Preisen, Buchgestaltung, Mitwirkenden, Lieferzeiten oder jedem Aspekt des Prozesses.',
    };
    setMessages([{ role: 'assistant', content: greetings[lang] || greetings.es }]);
  }, [i18n.language]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    const newMsgs = [...messages, { role: 'user', content: text }];
    setMessages(newMsgs);
    setSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/help/chat`, {
        message: text,
        session_id: sessionId.current,
        language: i18n.language || 'es',
      }, { withCredentials: true });
      const reply = { role: 'assistant', content: data.response };
      setMessages([...newMsgs, reply]);
      if (voiceEnabled) speak(data.response, newMsgs.length);
    } catch (err) {
      toast.error(t('help.chatError', 'No pude responder, inténtalo de nuevo.'));
    } finally {
      setSending(false);
    }
  };

  const speak = (text, idx) => {
    if (!synthRef.current) return;
    synthRef.current.cancel();
    const clean = text.replace(/[#*_`~]/g, '').replace(/\n+/g, '. ');
    const utter = new SpeechSynthesisUtterance(clean);
    const langMap = { es: 'es-ES', en: 'en-US', fr: 'fr-FR', de: 'de-DE' };
    utter.lang = langMap[i18n.language] || 'es-ES';
    utter.rate = 1.0;
    utter.pitch = 1.05;
    utter.onstart = () => setCurrentlyPlaying(idx);
    utter.onend = () => setCurrentlyPlaying(null);
    utter.onerror = () => setCurrentlyPlaying(null);
    synthRef.current.speak(utter);
  };

  const stopSpeaking = () => {
    synthRef.current?.cancel();
    setCurrentlyPlaying(null);
  };

  return (
    <div className="flex flex-col h-[600px] bg-white rounded-2xl border shadow-sm" data-testid="help-chatbot">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-amber-50 to-rose-50 rounded-t-2xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-rose-400 flex items-center justify-center text-white shadow-sm">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <div className="font-semibold text-slate-800">{t('help.chatbot.title', 'Asistente Memories')}</div>
            <div className="text-xs text-slate-500">{t('help.chatbot.subtitle', 'Pregúntame lo que quieras sobre la app')}</div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { setVoiceEnabled(!voiceEnabled); if (voiceEnabled) stopSpeaking(); }}
          className="text-slate-600 hover:text-amber-600"
          data-testid="toggle-voice-btn"
          title={voiceEnabled ? t('help.chatbot.voiceOff', 'Voz activada') : t('help.chatbot.voiceOn', 'Activar voz')}
        >
          {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </Button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`flex items-start gap-2 max-w-[85%] ${m.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${
                m.role === 'user' ? 'bg-slate-700 text-white' : 'bg-gradient-to-br from-amber-400 to-rose-400 text-white'
              }`}>
                {m.role === 'user' ? <User className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
              </div>
              <div className={`rounded-2xl px-4 py-2.5 ${
                m.role === 'user'
                  ? 'bg-slate-700 text-white rounded-tr-sm'
                  : 'bg-white border border-slate-200 text-slate-800 rounded-tl-sm shadow-sm'
              }`}>
                <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.content}</div>
                {m.role === 'assistant' && i > 0 && (
                  <button
                    onClick={() => currentlyPlaying === i ? stopSpeaking() : speak(m.content, i)}
                    className="mt-2 text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1"
                    data-testid={`speak-msg-${i}`}
                  >
                    {currentlyPlaying === i
                      ? <><VolumeX className="w-3 h-3" /> {t('help.chatbot.stopListening', 'Detener')}</>
                      : <><Volume2 className="w-3 h-3" /> {t('help.chatbot.listen', 'Escuchar')}</>}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 bg-white border rounded-2xl px-4 py-3 shadow-sm">
              <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{animationDelay:'0ms'}} />
              <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{animationDelay:'150ms'}} />
              <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{animationDelay:'300ms'}} />
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="p-3 border-t bg-white rounded-b-2xl flex gap-2"
      >
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t('help.chatbot.placeholder', 'Escribe tu pregunta...')}
          disabled={sending}
          className="flex-1"
          data-testid="chatbot-input"
        />
        <Button type="submit" disabled={sending || !input.trim()} data-testid="chatbot-send-btn" className="bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>
    </div>
  );
};

export default HelpChatbot;
